import React from 'react'

import {
  BorderStyle,
  Box,
  ButtonColorVariant,
  Color,
  Heading5,
  Modal,
  ModalBody,
  ModalButton,
  ModalClose,
} from '@gousto-internal/citrus-react'
import { PortionSizeSelector } from 'PortionSizeSelector/PortionSizeSelector'

import { availablePortions, type PortionSizes } from 'config/portionSizes'

import { useEditBoxSize } from '../useEditBoxSize'
import { NoticePanel } from './NoticePanel'

// Sizes for which PortionSizeSelector renders the Heads-up tip.
const SIZES_WITH_RECIPE_CARD_TIP: PortionSizes[] = [1, 5]

export type EditBoxSizeModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirmComplete: () => void
}

export const EditBoxSizeModal = ({ isOpen, onClose, onConfirmComplete }: EditBoxSizeModalProps) => {
  const {
    selectedSize,
    setSelectedSize,
    basketWillBeEmptied,
    menuWillBeUpdated,
    isConfirmDisabled,
    confirm,
  } = useEditBoxSize()

  // Unmount when closed so hook state resets each time the modal reopens.
  if (!isOpen) return null

  const handleConfirm = () => {
    // confirm() runs the redux flow (box-size change + basket reset).
    // onConfirmComplete() is the parent's UI cleanup (close modal + dropdown).
    confirm()
    onConfirmComplete()
  }

  return (
    <Modal isOpen name="edit-box-size-modal" onRequestClose={onClose}>
      <ModalClose onClose={onClose} />
      <Box
        paddingH={4}
        paddingV={4}
        borderColor={Color.ColdGrey_100}
        borderBottomWidth={0.5}
        borderStyle={BorderStyle.Solid}
      >
        <Heading5 size={7}>Edit box size</Heading5>
      </Box>
      <ModalBody paddingV={6}>
        {/* Top up the selector's trailing space when its Heads-up tip is shown. */}
        <Box paddingBottom={SIZES_WITH_RECIPE_CARD_TIP.includes(selectedSize) ? 2 : 0}>
          <PortionSizeSelector
            portionSize={selectedSize}
            availablePortionSizes={availablePortions as PortionSizes[]}
            onChange={(e) => setSelectedSize(Number(e.target.value) as PortionSizes)}
          />
        </Box>
        {basketWillBeEmptied && (
          <Box paddingBottom={2}>
            <NoticePanel
              variant="warning"
              title="Your recipe selection will be reset"
              body="Changing your box size will remove the recipes you’ve already chosen from your basket."
            />
          </Box>
        )}
        {menuWillBeUpdated && (
          <Box paddingBottom={2}>
            <NoticePanel
              variant="info"
              body="By editing your box size, what recipes are available will update to match this selection."
            />
          </Box>
        )}
        <ModalButton
          colorVariant={ButtonColorVariant.Primary}
          width="100%"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
        >
          Confirm box size
        </ModalButton>
      </ModalBody>
    </Modal>
  )
}
